import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useSearchParams } from 'react-router-dom';
import { Toaster } from '@/components/ui/toaster';
import { ThemeProvider } from "@/components/ThemeProvider";
import { ConstantsProvider } from '@/contexts/ConstantsContext';
import Navbar from '@/components/layout/Navbar';
import Footer from '@/components/layout/Footer';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import RoleBasedRedirect from '@/components/auth/RoleBasedRedirect';
import useAuthCheck from '@/hooks/useAuthCheck';
import useInactivityLogout from '@/hooks/useInactivityLogout';
import useSessionCheck from '@/hooks/useSessionCheck';

import LandingPage from '@/pages/LandingPage';
import ContactPage from '@/pages/ContactPage';
import Auth from '@/pages/auth/Auth';
import ErrorPage from '@/pages/ErrorPage';

import ClientDashboard from '@/pages/client/ClientDashboard';
import ClientRequests from '@/pages/client/ClientRequests';
import ClientOffers from '@/pages/client/ClientOffers';
import ClientHistory from '@/pages/client/ClientHistory';
import ClientTracking from '@/pages/client/ClientTracking';
import ClientPayments from '@/pages/client/ClientPayments';
import ClientSettings from '@/pages/client/ClientSettings';
import ClientOperations from '@/pages/client/ClientOperations';
import ClientRequestDetails from '@/pages/client/ClientRequestDetails';
import TransporterDashboard from '@/pages/transporter/TransporterDashboard';
import TransporterMissionsRequests from '@/pages/transporter/TransporterMissionsRequests';
import TransporterMissions from '@/pages/transporter/TransporterMissions';
import TransporterRequests from '@/pages/transporter/TransporterRequests';
import TransporterVehicles from '@/pages/transporter/TransporterVehicles';
import TransporterDrivers from '@/pages/transporter/TransporterDrivers';
import TransporterEarnings from '@/pages/transporter/TransporterEarnings';
import TransporterHistory from '@/pages/transporter/TransporterHistory';
import TransporterSettings from '@/pages/transporter/TransporterSettings';

import AdminDashboard from '@/pages/admin/AdminDashboard';
import AdminUsers from '@/pages/admin/AdminUsers';
import UserProfile from '@/pages/admin/UserProfile';
import AdminValidations from '@/pages/admin/AdminValidations';
import AdminDisputes from '@/pages/admin/AdminDisputes';
import AdminTransporters from '@/pages/admin/AdminTransporters';
import AdminClients from '@/pages/admin/AdminClients';
import AdminRequestDetails from '@/pages/admin/AdminRequestDetails';
import AdminOfferDetails from '@/pages/admin/AdminOfferDetails';
import AdminFinancials from '@/pages/admin/AdminFinancials';
import AdminSupport from '@/pages/admin/AdminSupport';
import AdminGeo from '@/pages/admin/AdminGeo';
import AdminSettings from '@/pages/admin/AdminSettings';
import AdminCommunications from '@/pages/admin/AdminCommunications';
import AdminOperations from '@/pages/admin/AdminOperations';

// Layout pour les pages publiques (Landing, etc.)
const LandingLayout = ({ children }) => (
  <div className="min-h-screen bg-background text-foreground light">
    <Navbar />
    <main>{children}</main>
    <Footer />
  </div>
);

// Routes protégées pour les dashboards avec ThemeProvider
const DashboardRoutes = () => (
  <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
    <Routes>
      {/* Route de redirection intelligente basée sur le rôle */}
      <Route path="dashboard" element={<RoleBasedRedirect />} />

      {/* Routes Client */}
      <Route
        path="client/dashboard"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <ClientDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="client/operations"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <ClientOperations />
          </ProtectedRoute>
        }
      />
      <Route
        path="client/operations/:id"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <ClientRequestDetails />
          </ProtectedRoute>
        }
      />
      {/* Redirections vers la page consolidée */}
      <Route
        path="client/requests"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <Navigate to="/client/operations?tab=requests" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="client/offers"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <Navigate to="/client/operations?tab=offers" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="client/tracking"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <Navigate to="/client/operations?tab=tracking" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="client/payments"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <Navigate to="/client/operations?tab=finances" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="client/history"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <Navigate to="/client/operations?tab=history" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="client/settings"
        element={
          <ProtectedRoute allowedRoles={['CLIENT']}>
            <ClientSettings />
          </ProtectedRoute>
        }
      />

      {/* Routes Transporteur */}
      <Route
        path="transporter/dashboard"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <TransporterDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="transporter/missions-requests"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <TransporterMissionsRequests />
          </ProtectedRoute>
        }
      />
      {/* Routes de compatibilité - redirigent vers l'interface unifiée */}
      <Route
        path="transporter/missions"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <Navigate to="/transporter/missions-requests?tab=missions" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="transporter/requests"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <Navigate to="/transporter/missions-requests?tab=requests" replace />
          </ProtectedRoute>
        }
      />
      <Route
        path="transporter/vehicles"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <TransporterVehicles />
          </ProtectedRoute>
        }
      />
      <Route
        path="transporter/drivers"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <TransporterDrivers />
          </ProtectedRoute>
        }
      />
      <Route
        path="transporter/earnings"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <TransporterEarnings />
          </ProtectedRoute>
        }
      />
      <Route
        path="transporter/history"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <TransporterHistory />
          </ProtectedRoute>
        }
      />
      <Route
        path="transporter/settings"
        element={
          <ProtectedRoute allowedRoles={['TRANSPORTEUR']}>
            <TransporterSettings />
          </ProtectedRoute>
        }
      />

      {/* Routes Admin - Accessibles par ADMIN et AGENT */}
      <Route
        path="admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/operations"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminOperations />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/users"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminUsers />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/user/:id"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <UserProfile />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/transporters"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminTransporters />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/clients"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminClients />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/clients/:clientId/requests/:requestId"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminRequestDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/transporters/:transporterId/offers/:offerId"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminOfferDetails />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/financials"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminFinancials />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/support"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminSupport />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/geo-analytics"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminGeo />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/communications"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminCommunications />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/settings"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminSettings />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/validations"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminValidations />
          </ProtectedRoute>
        }
      />
      <Route
        path="admin/disputes"
        element={
          <ProtectedRoute allowedRoles={['ADMIN', 'AGENT']}>
            <AdminDisputes />
          </ProtectedRoute>
        }
      />

      {/* Routes d'erreur - doivent être accessibles sans authentification */}
      <Route path="error" element={<ErrorPage />} />
      <Route path="error/:code" element={<ErrorPage />} />

      {/* Route par défaut - Page d'erreur 404 pour routes non trouvées */}
      {/* Cette route doit être la dernière et ne nécessite pas d'authentification */}
      <Route path="*" element={<ErrorPage />} />
    </Routes>
  </ThemeProvider>
);

// Composant interne qui utilise useAuthCheck
const AppContent = () => {
  // Vérifier l'authentification au démarrage
  useAuthCheck();

  // Déconnexion automatique après 5 minutes d'inactivité
  useInactivityLogout(5);

  // Vérifier périodiquement la validité de la session (toutes les 15 secondes)
  useSessionCheck(15000);

  return (
    <div className="min-h-screen">
      <Routes>
        {/* Pages publiques */}
        <Route path="/" element={<LandingLayout><LandingPage /></LandingLayout>} />
        <Route path="/contact" element={<LandingLayout><ContactPage /></LandingLayout>} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/auth/login" element={<Navigate to="/auth?tab=login" replace />} />
        <Route path="/auth/register" element={<Navigate to="/auth?tab=register" replace />} />

        {/* Anciennes routes pour compatibilité */}
        <Route path="/login" element={<Navigate to="/auth?tab=login" replace />} />
        <Route path="/register" element={<Navigate to="/auth?tab=register" replace />} />

        {/* Routes d'erreur publiques */}
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/error/:code" element={<ErrorPage />} />

        {/* Toutes les autres routes (dashboards protégés) */}
        <Route path="/*" element={<DashboardRoutes />} />
      </Routes>
      <Toaster />
    </div>
  );
};

function App() {
  return (
    <ConstantsProvider>
      <Router>
        <AppContent />
      </Router>
    </ConstantsProvider>
  );
}

export default App;
